import React from "react";
import { useLocation } from "react-router-dom";

import eventPageQuery from "./event.graphql";
import useNodeQuery from "@default/hooks/use-node-query";
import NodeWrapper from "@default/nodes/shared-components/node-wrapper";
import OpenGraphMeta from "@default/js/general-components/seo/open-graph-meta";
import { stripHtml } from "@default/js/general-components/seo/utils";
import { self } from "@default/js/config";

import Appearances from "@default/nodes/shared-components/appearances/appearances";
import MetaInfos from "@default/js/participants/components/meta-infos";
import DeezerWidget from "@default/js/participants/components/deezer-widget";
import LookingFor from "@default/js/participants/components/looking-for";
import SocialLinks from "@default/js/participants/components/social-links";
import Related from "@default/js/participants/components/related/related";
import DetailPageIntro from "@default/nodes/shared-components/detail-page-intro";
import ParticipatingPersons from "@default/nodes/shared-components/participating-persons";
import ShowcaseActs from "@default/nodes/shared-components/participating-acts";
import LinkedEvents from "@default/teasers/event/components/linked-events";
import Sponsors from "@default/js/participants/components/sponsors";
import CheckForChildren from "@default/lib/check-for-children";
import Credit from "@default/nodes/shared-components/credit";
import MainPresenterTextInfo from "@default/nodes/shared-components/main-presenter-text-info";
import MainPresenterSponsoredImage from "@default/nodes/shared-components/main-presenter-sponsored-image";
import useAccessibilityContext from "@default/js/accessibility/store/use-accessibility-context";
import ArchivedEventInfo from "@default/nodes/shared-components/archived-event-info";
import SpotifyWidget from "@default/js/participants/components/spotify-widget/spotify-widget";

const Event = () => {
  const { entity, loading, error } = useNodeQuery(eventPageQuery);
  const { simpleDesign } = useAccessibilityContext();
  const { pathname } = useLocation();

  return (
    <NodeWrapper entity={entity} loading={loading} error={error}>
      {entity && (
        <OpenGraphMeta
          title={entity.title || entity.label}
          type="website"
          url={self + pathname}
          description={stripHtml(entity.fieldSubline || entity.fieldText)}
          image={
            entity.fieldImage?.fieldMediaImage?.style?.url
              ? {
                  url: entity.fieldImage.fieldMediaImage.style.url,
                  alt: entity.fieldImage.fieldMediaImage.alt,
                }
              : undefined
          }
        />
      )}
      <DetailPageIntro content={entity} mode={"event"} />
      <div className="container max-width main-container">
        <div className="row infos">
          <div className={"col-12 col-lg-9 appearances-wrapper"}>
            <Appearances
              detailedMode={true}
              content={entity}
              type={"event"}
              showAddToCalendar={true}
              viewMode={"detail"}
              showEndDate={true}
            />
          </div>

          {!simpleDesign && (
            <div className={"col-12 col-lg-3 meta-info-wrapper"}>
              <MetaInfos content={entity} />
            </div>
          )}
        </div>

        <div className="row text-wrapper">
          <ArchivedEventInfo content={entity} />

          <MainPresenterSponsoredImage content={entity} />
          <MainPresenterTextInfo content={entity} />

          {entity?.fieldText && (
            <div className="col-12 text-wrapper">
              <div
                className={"text headline-subhead"}
                dangerouslySetInnerHTML={{ __html: entity.fieldText }}
              />
            </div>
          )}

          <Credit content={entity} />
        </div>

        <div className="row with-gaps">
          <LinkedEvents content={entity} />

          <ParticipatingPersons content={entity} />

          <ShowcaseActs content={entity} />

          <CheckForChildren classes={"col-12 col-lg-6 left-col"}>
            <SpotifyWidget content={entity} />
            <DeezerWidget content={entity} />
            <LookingFor content={entity} />
            <SocialLinks content={entity} />
          </CheckForChildren>

          <CheckForChildren classes={"col-12 col-lg-6 right-col"}>
            <Sponsors content={entity} />
          </CheckForChildren>
        </div>
      </div>
      <CheckForChildren className="related-container">
        <Related content={entity} type={"event"} />
      </CheckForChildren>
    </NodeWrapper>
  );
};

Event.propTypes = {};

export default Event;
